import { LoanStage } from "@toorak/tc-common-fe-sdk";
import { LoanStatusEnum } from "@toorak/tc-common-util-sdk";
import type { LoanStatus } from "./initiatePurchaseOfac";

/** Header chip visual for the Loan Detail tag row (T9). */
export interface OfacHeaderChipSpec {
  label: string;
  style: { backgroundColor: string; color: string };
}

const HIDDEN_LOAN_STATES: ReadonlySet<LoanStatusEnum> = new Set([
  LoanStatusEnum.DataEntry,
  LoanStatusEnum.ExcelError,
  LoanStatusEnum.ExcelSuccess,
  LoanStatusEnum.Submit,
  LoanStatusEnum.Withdrawn,
  LoanStatusEnum.Rejected,
  LoanStatusEnum.Delete
]);

/**
 * Show the OFAC header chip from Initial Review onward on Pre/Post-Close,
 * never for Data Entry / Excel / Submit / Withdrawn / Rejected / Delete,
 * and never on FES / Toorval (OFAC tab is Pre + Post only).
 */
export function shouldShowOfacHeaderChip(input: {
  loanState: LoanStatusEnum | string | null | undefined;
  loanStage: LoanStage | string | null | undefined;
}): boolean {
  const { loanState, loanStage } = input;
  if (!loanState || !loanStage) return false;
  if (loanStage !== LoanStage.pre && loanStage !== LoanStage.post) return false;
  return !HIDDEN_LOAN_STATES.has(loanState as LoanStatusEnum);
}

const CHIP_NOT_SCREENED: OfacHeaderChipSpec = {
  label: "Sanctions: NOT SCREENED",
  style: { backgroundColor: "#fb623f", color: "#ffffff" }
};

/**
 * Map LN loan_status to a header chip. Fail-closed: never look cleared unless
 * the API says OFAC CLEARED. REVIEW PENDING uses the mock's "NEEDS REVIEW" copy.
 * Missing / invalid status → Not Screened (same as OFAC NOT SCREENED).
 */
export function ofacHeaderChipSpec(
  status: LoanStatus | null | undefined
): OfacHeaderChipSpec {
  switch (status) {
    case "OFAC CLEARED":
      return {
        label: "Sanctions: CLEARED",
        style: { backgroundColor: "#2fce89", color: "#ffffff" }
      };
    case "OFAC REVIEW PENDING":
      return {
        label: "Sanctions: NEEDS REVIEW",
        style: { backgroundColor: "#fb623f", color: "#ffffff" }
      };
    case "OFAC TRUE MATCH":
      return {
        label: "Sanctions: TRUE MATCH",
        style: { backgroundColor: "#f75576", color: "#ffffff" }
      };
    case "OFAC NOT SCREENED":
    default:
      return CHIP_NOT_SCREENED;
  }
}

/** Pick this loan's status from a POST /bff/ofac/status body. */
export function pickOfacLoanStatus(
  response: { loans?: { source_loan_id: string; loan_status?: string }[] } | null | undefined,
  loanId: string
): LoanStatus {
  const row = response?.loans?.find((l) => l.source_loan_id === loanId);
  const status = row?.loan_status;
  if (
    status === "OFAC CLEARED" ||
    status === "OFAC REVIEW PENDING" ||
    status === "OFAC TRUE MATCH" ||
    status === "OFAC NOT SCREENED"
  ) {
    return status;
  }
  // Missing / invalid / legacy Unknown → Not Screened (fail closed).
  return "OFAC NOT SCREENED";
}
